import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Sparkles, Mic, FileText, ArrowRight, ShieldCheck, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { DevistoLogo } from "@/components/DevistoLogo";
import { API, apiError, TRADE_OPTIONS } from "@/lib/studioApi";
import { useMarket, vatLine, priceLabel } from "@/market";
import { useLanguage } from "@/i18n";
import { setAppLang } from "@/i18n/appLang";

const BENEFITS = (copy, market, t, lang) => [
  { icon: Mic, title: t("Dictez, ne tapez plus"), line: t("Décrivez le chantier à voix haute, l'assistant chiffre le devis avec vos prix.") },
  { icon: Sparkles, title: t("Devis prêt en 90 secondes"), line: `${t("Postes, quantités,")} ${vatLine(market, lang)} : ${t("vous validez, le client signe sur place.")}` },
  { icon: FileText, title: t(copy.register_benefit.title), line: t(copy.register_benefit.line) },
];

const TRUST = (market, t) => [t("30 jours gratuits"), t("Sans carte bancaire"), t("Sans engagement"), `${t("Puis")} ${priceLabel(market)}/${t("mois")}, ${t("ouvriers inclus")}`];

const field =
  "w-full rounded-xl border border-[#E2E7F7] bg-white px-4 py-3 text-[15px] text-[#0F2645] placeholder:text-[#93A0BC] outline-none transition-all duration-200 focus:border-[#3A5CC8] focus:ring-2 focus:ring-[#3A5CC8]/20";

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-[#5C6A87]">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-[11px] text-[#93A0BC]">{hint}</span>}
  </label>
);

const PHONE = { BE: "+32 4xx xx xx xx", FR: "+33 6 xx xx xx xx", ES: "+34 6xx xxx xxx", LU: "+352 6xx xxx xxx" };
const TLD = { BE: "be", FR: "fr", ES: "es", LU: "lu" };

export default function StudioRegister() {
  const { lang: siteLang, t } = useLanguage();
  useEffect(() => { setAppLang(siteLang); return () => setAppLang("fr"); }, [siteLang]);
  const navigate = useNavigate();
  const { country, setCountry, countries, market, copy } = useMarket();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", vat: "", trade: "chauffagiste", password: "", country, tax_ids: {} });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm((f) => (f.country === country ? f : { ...f, country, tax_ids: {}, vat: country === "BE" ? f.vat : "" })); }, [country]);
  const countryRules = countries.find((c) => c.country === form.country);
  const setTax = (k) => (e) => setForm((f) => ({ ...f, tax_ids: { ...f.tax_ids, [k]: e.target.value } }));
  const benefits = BENEFITS(copy, market, t, siteLang);
  const trust = TRUST(market, t);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/studio/register`, { ...form, lang: localStorage.getItem("devisto_lang") || "fr" });
      localStorage.setItem("devisto_token", data.token);
      localStorage.setItem("devisto_token_at", String(Date.now()));
      localStorage.setItem("devisto_role", data.role);
      localStorage.setItem("devisto_name", data.name || "");
      toast.success("Bienvenue dans Devisto Studio.");
      navigate("/studio/bienvenue");
    } catch (err) {
      toast.error(apiError(err, "Inscription impossible."));
    } finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="font-body min-h-screen bg-[#F3F5FC]">
      <div className="mx-auto max-w-6xl px-5 py-10 lg:py-14">
        <Link to="/" data-testid="register-logo"><DevistoLogo className="h-7 w-auto" /></Link>
        <div className="mt-10 grid grid-cols-1 items-start gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
          {/* ---------- Argumentaire ---------- */}
          <div className="dv-card-in">
            <span data-testid="register-market-badge" className="inline-flex items-center gap-2 rounded-full border border-[#3A5CC8]/30 bg-[#EEF1FC] px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#3A5CC8]">
              {form.country === "BE" ? (
                <span className="inline-flex h-3 w-[13px] shrink-0 overflow-hidden rounded-[2px]" aria-hidden="true">
                  <span className="h-full w-1/3 bg-[#1A1A1A]" /><span className="h-full w-1/3 bg-[#FFD100]" /><span className="h-full w-1/3 bg-[#C8102E]" />
                </span>
              ) : <span aria-hidden="true" className="text-sm leading-none">{market.flag}</span>}
              {t(copy.badge)}
            </span>
            <h1 className="mt-6 font-heading text-4xl font-extrabold tracking-tight text-[#0F2645] sm:text-5xl lg:text-6xl lg:leading-[1.05]">
              Du chantier <span className="text-[#3A5CC8]">à la facture</span>
            </h1>
            <p data-testid="register-market-lead" className="mt-5 max-w-lg text-base leading-relaxed text-[#5C6A87] md:text-lg">
              {t(copy.register_lead)}
            </p>
            <div className="mt-9 space-y-5">
              {benefits.map((b, i) => (
                <div key={b.title} className="dv-card-in flex items-start gap-4" style={{ animationDelay: `${120 + i * 80}ms` }}>
                  <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0F2645] text-white shadow-[0_8px_20px_-10px_rgba(8,16,77,0.8)]">
                    <b.icon className="h-4 w-4 text-[#9FB4F2]" />
                  </span>
                  <div>
                    <p className="font-heading text-sm font-bold text-[#0F2645]">{b.title}</p>
                    <p className="mt-0.5 text-sm leading-relaxed text-[#5C6A87]">{b.line}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 rounded-2xl border border-[#E2E7F7] bg-white p-5">
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#93A0BC]">Ce que disent les artisans</p>
              <p data-testid="register-testimonial" className="mt-2 font-heading text-base font-semibold leading-snug text-[#0F2645]">
                {t(copy.testimonial.quote)}
              </p>
              <p className="mt-2 text-xs text-[#5C6A87]">{t(copy.testimonial.author)}</p>
            </div>
          </div>

          {/* ---------- Formulaire ---------- */}
          <form
            onSubmit={submit}
            data-testid="studio-register-form"
            className="dv-card-in order-first h-fit rounded-2xl border border-[#E2E7F7] bg-white p-6 shadow-[0_24px_60px_-30px_rgba(8,16,77,0.35)] sm:p-8 lg:order-none"
            style={{ animationDelay: "80ms" }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-heading text-xl font-extrabold tracking-tight text-[#0F2645]">Créer mon compte artisan</h2>
                <p className="mt-1 text-sm text-[#5C6A87]">Deux minutes, et votre premier devis est prêt.</p>
              </div>
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700">
                <ShieldCheck className="h-3.5 w-3.5" /> Essai 30 j
              </span>
            </div>

            <div className="mt-6 rounded-2xl border border-[#E2E7F7] bg-[#F6F8FD] p-4">
              <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-[#5C6A87]">Pays de votre entreprise *</span>
              <div data-testid="reg-country-tiles" role="radiogroup" className="grid grid-cols-4 gap-1.5">
                {countries.map((c) => (
                  <button key={c.country} type="button" role="radio" aria-checked={c.country === form.country} data-testid={`reg-country-${c.country}`} onClick={() => setCountry(c.country)}
                    className={`flex min-h-[56px] flex-col items-center justify-center gap-0.5 rounded-xl border text-center transition-colors ${c.country === form.country ? "border-[#3A5CC8] bg-white text-[#0F2645] shadow-[0_6px_16px_-10px_rgba(58,92,200,0.8)]" : "border-transparent bg-transparent text-[#5C6A87] hover:bg-white"}`}>
                    <span className="text-xl leading-none" aria-hidden="true">{c.flag}</span>
                    <span className="text-[10px] font-bold">{c.country}</span>
                  </button>
                ))}
              </div>
              <select data-testid="reg-country" className="sr-only" tabIndex={-1} aria-hidden="true" value={form.country} onChange={(e) => setCountry(e.target.value)}>
                {countries.map((c) => <option key={c.country} value={c.country}>{c.name}</option>)}
              </select>
              <p data-testid="reg-country-summary" className="mt-2.5 text-[11px] text-[#5C6A87]">
                <span className="font-bold text-[#0F2645]">{t(market.name)}</span> · {market.currency} · {vatLine(market, siteLang)} · {priceLabel(market)}/{t("mois")} {t("après l'essai. Modifiable ensuite dans le profil.")}
              </p>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Votre nom *">
                <input data-testid="reg-name" required className={field} placeholder="Marc Dupont" value={form.name} onChange={set("name")} />
              </Field>
              <Field label="Entreprise">
                <input data-testid="reg-company" className={field} placeholder="Chauffage Dupont SRL" value={form.company} onChange={set("company")} />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Métier principal *">
                  <select data-testid="reg-trade" className={field} value={form.trade} onChange={set("trade")}>
                    {TRADE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Email *">
                <input data-testid="reg-email" required type="email" className={field} placeholder={`vous@entreprise.${TLD[form.country] || "be"}`} value={form.email} onChange={set("email")} />
              </Field>
              <Field label="Téléphone">
                <input data-testid="reg-phone" className={field} placeholder={PHONE[form.country] || PHONE.BE} value={form.phone} onChange={set("phone")} />
              </Field>
              {form.country === "BE" && (
              <Field label="N° de TVA" hint="Facultatif maintenant, requis pour activer Peppol.">
                <input data-testid="reg-vat" className={`${field} font-mono`} placeholder="BE 0123.456.789" value={form.vat} onChange={set("vat")} />
              </Field>
              )}
              {countryRules?.tax_ids?.length > 0 && (
                <div data-testid="reg-tax-ids" className="grid gap-3 sm:col-span-2 sm:grid-cols-2">
                  {countryRules.tax_ids.filter((t) => !(form.country === "BE" && t.key === "vat")).map((t) => (
                    <Field key={t.key} label={`${t.label}${t.required ? " *" : ""}`}>
                      {t.type === "select" ? (
                        <select data-testid={`reg-tax-${t.key}`} required={t.required} className={field} value={form.tax_ids[t.key] || ""} onChange={setTax(t.key)}>
                          <option value="">— choisir</option>
                          {(t.options || []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      ) : (
                        <input data-testid={`reg-tax-${t.key}`} required={t.required} className={`${field} font-mono`} placeholder={t.placeholder || ""} value={form.tax_ids[t.key] || ""} onChange={setTax(t.key)} />
                      )}
                    </Field>
                  ))}
                </div>
              )}
              <Field label="Mot de passe *" hint="6 caractères minimum.">
                <input data-testid="reg-password" required type="password" minLength={6} className={field} placeholder="••••••••" value={form.password} onChange={set("password")} />
              </Field>
            </div>

            <button
              type="submit"
              data-testid="reg-submit"
              disabled={saving}
              className="mt-6 inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-[#3A5CC8] px-6 text-base font-bold text-white shadow-[0_12px_28px_-12px_rgba(58,92,200,0.9)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#3B5ECB] active:translate-y-0 active:scale-[0.99] disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {saving ? "Création…" : "Commencer gratuitement"} {!saving && <ArrowRight className="h-4 w-4" />}
            </button>

            <ul className="mt-5 grid grid-cols-2 gap-x-3 gap-y-1.5">
              {trust.map((x) => (
                <li key={x} className="inline-flex items-center gap-1.5 text-xs text-[#5C6A87]">
                  <Check className="h-3.5 w-3.5 shrink-0 text-[#3A5CC8]" /> {x}
                </li>
              ))}
            </ul>

            <p className="mt-5 border-t border-[#E2E7F7] pt-4 text-center text-xs text-[#5C6A87]">
              Déjà un compte ? <Link to="/connexion" data-testid="reg-login-link" className="font-bold text-[#3A5CC8] hover:underline">Se connecter</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
